import React, { useEffect, useState, useRef, useCallback } from "react";
import "./App.css";

const API_HOST = process.env.REACT_APP_API_HOST || "http://localhost:8000";

// ===== ROI Cell với Grid Editor (chỉnh trực tiếp trên ảnh, tự cập nhật) =====
function RoiCell({ item, index, onSaveEdit, onRematch, rematchLoading }) {
  const [editBounds, setEditBounds] = useState(item.gridBounds || { x1: 1/3, x2: 2/3, y1: 1/3, y2: 2/3 });
  const [previewDataUrl, setPreviewDataUrl] = useState(null);
  const containerRef = useRef(null);
  const dragRef = useRef({ line: null });
  const lastBoundsRef = useRef(editBounds);

  const { width, height, baseDataUrl, dotsData } = item;

  const drawPreview = useCallback((bounds) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(width * bounds.x1, 0);
      ctx.lineTo(width * bounds.x1, height);
      ctx.moveTo(width * bounds.x2, 0);
      ctx.lineTo(width * bounds.x2, height);
      ctx.moveTo(0, height * bounds.y1);
      ctx.lineTo(width, height * bounds.y1);
      ctx.moveTo(0, height * bounds.y2);
      ctx.lineTo(width, height * bounds.y2);
      ctx.stroke();
      ctx.fillStyle = "#ef4444";
      (dotsData || []).forEach(({ cx, cy, radius }) => {
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
      });
      setPreviewDataUrl(canvas.toDataURL("image/png"));
    };
    img.src = baseDataUrl || item.dataUrl;
  }, [width, height, baseDataUrl, dotsData]);

  useEffect(() => {
    const bounds = item.gridBounds || { x1: 1/3, x2: 2/3, y1: 1/3, y2: 2/3 };
    setEditBounds(bounds);
    lastBoundsRef.current = bounds;
  }, [item.gridBounds]);

  useEffect(() => {
    drawPreview(editBounds);
    lastBoundsRef.current = editBounds;
  }, [editBounds, drawPreview]);

  const handleMouseDown = (e, line) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { line };
  };

  const commitEdit = useCallback((bounds) => {
    const dotsToDraw = item.dotsData || [];
    const src = item.baseDataUrl || item.dataUrl;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(width * bounds.x1, 0);
      ctx.lineTo(width * bounds.x1, height);
      ctx.moveTo(width * bounds.x2, 0);
      ctx.lineTo(width * bounds.x2, height);
      ctx.moveTo(0, height * bounds.y1);
      ctx.lineTo(width, height * bounds.y1);
      ctx.moveTo(0, height * bounds.y2);
      ctx.lineTo(width, height * bounds.y2);
      ctx.stroke();
      ctx.fillStyle = "#ef4444";
      dotsToDraw.forEach(({ cx, cy, radius }) => {
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
      });
      const newDataUrl = canvas.toDataURL("image/png");
      onSaveEdit(bounds, newDataUrl);
      onRematch(bounds);
    };
    img.src = src;
  }, [item.dotsData, width, height, item.baseDataUrl, item.dataUrl, onSaveEdit, onRematch]);

  useEffect(() => {
    const onMove = (e) => {
      const { line } = dragRef.current;
      if (!line) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const isVertical = line.startsWith("x");
      const frac = isVertical ? (e.clientX - rect.left) / rect.width : (e.clientY - rect.top) / rect.height;
      const clamped = Math.max(0.05, Math.min(0.95, frac));
      setEditBounds((prev) => {
        const next = { ...prev };
        if (line === "x1") next.x1 = Math.min(clamped, prev.x2 - 0.05);
        else if (line === "x2") next.x2 = Math.max(clamped, prev.x1 + 0.05);
        else if (line === "y1") next.y1 = Math.min(clamped, prev.y2 - 0.05);
        else if (line === "y2") next.y2 = Math.max(clamped, prev.y1 + 0.05);
        lastBoundsRef.current = next;
        return next;
      });
    };
    const onUp = () => {
      const { line } = dragRef.current;
      if (line) {
        commitEdit(lastBoundsRef.current);
      }
      dragRef.current = { line: null };
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [commitEdit]);

  return (
    <div className="detected-roi-cell">
      <div
        ref={containerRef}
        className="roi-image-wrapper"
        style={{ position: "relative" }}
        title="Drag green lines to adjust grid · Auto-updates on release"
      >
        <img
          src={previewDataUrl || item.dataUrl}
          alt={`ROI ${index + 1}`}
          className="detected-roi-image"
          style={{ pointerEvents: "none", cursor: "default" }}
        />
        <div
          className="grid-line grid-line-v"
          style={{ left: `${editBounds.x1 * 100}%` }}
          onMouseDown={(e) => handleMouseDown(e, "x1")}
        />
        <div
          className="grid-line grid-line-v"
          style={{ left: `${editBounds.x2 * 100}%` }}
          onMouseDown={(e) => handleMouseDown(e, "x2")}
        />
        <div
          className="grid-line grid-line-h"
          style={{ top: `${editBounds.y1 * 100}%` }}
          onMouseDown={(e) => handleMouseDown(e, "y1")}
        />
        <div
          className="grid-line grid-line-h"
          style={{ top: `${editBounds.y2 * 100}%` }}
          onMouseDown={(e) => handleMouseDown(e, "y2")}
        />
      </div>
      <div className="detected-roi-label">
        <span className="roi-number">#{index + 1}</span>
        {rematchLoading && <span className="roi-loading">...</span>}
      </div>
    </div>
  );
}

function App() {
  const [cvReady, setCvReady] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [status, setStatus] = useState("Loading OpenCV…");
  const [displayImageUrl, setDisplayImageUrl] = useState(null);
  const [croppedImages, setCroppedImages] = useState([]);
  const [roiPage, setRoiPage] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [claheImageUrl, setClaheImageUrl] = useState(null);
  const [useClahePreprocess, setUseClahePreprocess] = useState(false);
  const computeClientIdButtonRef = useRef(null);

  // ===== ICON CONFIG =====
    const ICON_IMAGES = [
    "/icons/shape-1.drawio.png",
    "/icons/shape-2.drawio.png",
    "/icons/shape-3.drawio.png",
    "/icons/shape-4.drawio.png",
    "/icons/shape-5.drawio.png",
    "/icons/shape-6.drawio.png",
    "/icons/shape-7.drawio.png",
    "/icons/shape-8.drawio.png",
    "/icons/shape-9.drawio.png",
    "/icons/shape-10.drawio.png",
    "/icons/shape-11.drawio.png",
  ];

  // index 0 → '3', 1 → '10', ..., 10 → '100'; null → 'na'
  const PATTERN_LIST = [
    "3",
    "10",
    "11",
    "18",
    "19",
    "30",
    "35",
    "42",
    "43",
    "71",
    "100",
    "na",
  ];

  const [selectedIcons, setSelectedIcons] = useState(Array(9).fill(null));
  const [activeCell, setActiveCell] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [shapeMatching, setShapeMatching] = useState([]);
  const [explicitlyCleared, setExplicitlyCleared] = useState(new Set());
  const [rematchLoading, setRematchLoading] = useState(false);
  const [roiShapeMatches, setRoiShapeMatches] = useState([]);

  // ===== OpenCV ready =====
  useEffect(() => {
    const check = () => {
      if (window.cv && window.cv.Mat) {
        setCvReady(true);
        setStatus("OpenCV ready. Choose an image.");
      }
    };
    check();
    const t = setInterval(check, 500);
    return () => clearInterval(t);
  }, []);

  // ===== File upload =====
  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    const url = URL.createObjectURL(f);
    setDisplayImageUrl(url);
    setCroppedImages([]);
    setRoiShapeMatches([]);
    setRoiPage(0);
    setClaheImageUrl(null);
  };

  // ===== Helper: Mat -> dataURL =====
  const matToUrl = (mat) => {
    const cv = window.cv;
    const c = document.createElement("canvas");
    c.width = mat.cols;
    c.height = mat.rows;
    cv.imshow(c, mat);
    return c.toDataURL("image/png");
  };

  // ===== Helper: File to base64 =====
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // ===== Helper: Apply CLAHE (adaptive histogram equalization) =====
  const applyCLAHE = (imageSrc) => {
    return new Promise((resolve) => {
      if (!cvReady || !window.cv) {
        resolve(imageSrc);
        return;
      }

      const cv = window.cv;
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, img.width, img.height);
        const src = cv.matFromImageData(imageData);

        const gray = new cv.Mat();
        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

        const grayClahe = new cv.Mat();
        try {
          let clahe = null;
          if (typeof cv.createCLAHE === "function") {
            clahe = cv.createCLAHE(3.0, new cv.Size(8, 8));
          } else if (typeof cv.CLAHE === "function") {
            clahe = new cv.CLAHE(3.0, new cv.Size(8, 8));
          }

          if (clahe) {
            clahe.apply(gray, grayClahe);
            clahe.delete();
          } else {
            cv.equalizeHist(gray, grayClahe);
          }
        } catch (err) {
          cv.equalizeHist(gray, grayClahe);
        }

        const rgb = new cv.Mat();
        cv.cvtColor(grayClahe, rgb, cv.COLOR_GRAY2RGB);

        const resultCanvas = document.createElement("canvas");
        resultCanvas.width = rgb.cols;
        resultCanvas.height = rgb.rows;
        cv.imshow(resultCanvas, rgb);
        const result = resultCanvas.toDataURL("image/jpeg", 0.95);

        src.delete();
        gray.delete();
        grayClahe.delete();
        rgb.delete();

        resolve(result);
      };
      img.src = imageSrc;
    });
  };

  // ===== Helper: Check if point/box is inside another box =====
  const isInsideBox = (innerBox, outerBox) => {
    const [ix1, iy1, ix2, iy2] = innerBox;
    const [ox1, oy1, ox2, oy2] = outerBox;
    
    return ix1 >= ox1 && iy1 >= oy1 && ix2 <= ox2 && iy2 <= oy2;
  };

  // ===== Helper: Render ROI với grid tùy chỉnh =====
  const renderRoiWithGrid = (item, gridBounds) => {
    return new Promise((resolve) => {
      const bounds = gridBounds || item.gridBounds || { x1: 1/3, x2: 2/3, y1: 1/3, y2: 2/3 };
      const img = new Image();
      img.onload = () => {
        const { width, height, dotsData } = item;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        ctx.strokeStyle = "#22c55e";
        ctx.lineWidth = 2;
        const col1 = width * bounds.x1;
        const col2 = width * bounds.x2;
        const row1 = height * bounds.y1;
        const row2 = height * bounds.y2;
        ctx.beginPath();
        ctx.moveTo(col1, 0);
        ctx.lineTo(col1, height);
        ctx.moveTo(col2, 0);
        ctx.lineTo(col2, height);
        ctx.moveTo(0, row1);
        ctx.lineTo(width, row1);
        ctx.moveTo(0, row2);
        ctx.lineTo(width, row2);
        ctx.stroke();
        ctx.fillStyle = "#ef4444";
        dotsData.forEach(({ cx, cy, radius }) => {
          ctx.beginPath();
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
          ctx.fill();
        });
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = item.baseDataUrl || item.dataUrl;
    });
  };

  // ===== Helper: Crop images from detections =====
  const cropImagesFromDetections = (imageSrc, watermarkDetections, allDetections) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const cropped = [];
        
        watermarkDetections.forEach((watermark, index) => {
          let x, y, width, height;
          let watermarkBbox;
          
          if (watermark.bbox && Array.isArray(watermark.bbox)) {
            const [x1, y1, x2, y2] = watermark.bbox;
            x = Math.max(0, Math.floor(x1));
            y = Math.max(0, Math.floor(y1));
            width = Math.min(img.width - x, Math.floor(x2 - x1));
            height = Math.min(img.height - y, Math.floor(y2 - y1));
            watermarkBbox = watermark.bbox;
          } else if (watermark.box && Array.isArray(watermark.box)) {
            const [x1, y1, x2, y2] = watermark.box;
            x = Math.max(0, Math.floor(x1));
            y = Math.max(0, Math.floor(y1));
            width = Math.min(img.width - x, Math.floor(x2 - x1));
            height = Math.min(img.height - y, Math.floor(y2 - y1));
            watermarkBbox = watermark.box;
          } else {
            x = Math.max(0, Math.floor(watermark.x || watermark.x1 || 0));
            y = Math.max(0, Math.floor(watermark.y || watermark.y1 || 0));
            width = Math.min(img.width - x, Math.floor(watermark.width || (watermark.x2 ? watermark.x2 - x : 0)));
            height = Math.min(img.height - y, Math.floor(watermark.height || (watermark.y2 ? watermark.y2 - y : 0)));
            watermarkBbox = [x, y, x + width, y + height];
          }

          if (width > 0 && height > 0) {
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
            
            // Find dots inside this watermark box (no class filter - dot_detection is already dots)
            const dotsInside = allDetections.filter((detection) => {
              if (detection.bbox && Array.isArray(detection.bbox)) {
                return isInsideBox(detection.bbox, watermarkBbox);
              }
              if (detection.box && Array.isArray(detection.box)) {
                return isInsideBox(detection.box, watermarkBbox);
              }
              return false;
            });
            
            // Chỉ hiển thị ROI có dots
            if (dotsInside.length > 0) {
              const defaultBounds = { x1: 1/3, x2: 2/3, y1: 1/3, y2: 2/3 };
              const col1 = width * defaultBounds.x1;
              const col2 = width * defaultBounds.x2;
              const row1 = height * defaultBounds.y1;
              const row2 = height * defaultBounds.y2;
              // Vẽ grid 3x3 (đường kẻ xanh lá)
              ctx.strokeStyle = "#22c55e";
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(col1, 0);
              ctx.lineTo(col1, height);
              ctx.moveTo(col2, 0);
              ctx.lineTo(col2, height);
              ctx.moveTo(0, row1);
              ctx.lineTo(width, row1);
              ctx.moveTo(0, row2);
              ctx.lineTo(width, row2);
              ctx.stroke();
              
              const dotsData = [];
              dotsInside.forEach((dot) => {
                let dotBbox;
                if (dot.bbox && Array.isArray(dot.bbox)) {
                  dotBbox = dot.bbox;
                } else if (dot.box && Array.isArray(dot.box)) {
                  dotBbox = dot.box;
                } else {
                  return;
                }
                const [dx1, dy1, dx2, dy2] = dotBbox;
                const cx = (dx1 + dx2) / 2 - x;
                const cy = (dy1 + dy2) / 2 - y;
                const dotW = dx2 - dx1;
                const dotH = dy2 - dy1;
                const radius = Math.max(1.5, Math.min(dotW, dotH) / 4);
                dotsData.push({ cx, cy, radius });
                ctx.fillStyle = "#ef4444";
                ctx.beginPath();
                ctx.arc(cx, cy, radius, 0, Math.PI * 2);
                ctx.fill();
              });
              
              const confidence = watermark.confidence || watermark.conf || 0;
              const roiBox = [x, y, x + width, y + height];
              cropped.push({
                id: index,
                dataUrl: canvas.toDataURL("image/png"),
                baseDataUrl: undefined,
                dotsData,
                width,
                height,
                roiBox,
                confidence,
                dotsCount: dotsInside.length,
                gridBounds: defaultBounds,
              });
              cropped[cropped.length - 1].baseDataUrl = (() => {
                const c2 = document.createElement("canvas");
                c2.width = width;
                c2.height = height;
                const ctx2 = c2.getContext("2d");
                ctx2.drawImage(img, x, y, width, height, 0, 0, width, height);
                return c2.toDataURL("image/png");
              })();
            }
          }
        });
        
        resolve(cropped);
      };
      img.src = imageSrc;
    });
  };

  // ===== Helper: Get color for class name =====
  const getClassColor = (className) => {
    const colorMap = {
      "dot": "#22c55e",                    // green
      "cipherdotwatermark": "#eab308",     // yellow for CipherDotWatermark
      "circle": "#3b82f6",                 // blue
      "square": "#f59e0b",                 // orange
      "triangle": "#ef4444",               // red
      "rectangle": "#8b5cf6",               // purple
      "star": "#ec4899",                   // pink
      "cross": "#06b6d4",                  // cyan
      "diamond": "#84cc16",                // lime
      "oval": "#f97316",                   // orange-red
      "hexagon": "#6366f1",                // indigo
    };
    
    if (className && colorMap[className.toLowerCase()]) {
      return colorMap[className.toLowerCase()];
    }
    
    // Default colors for unknown classes
    const defaultColors = [
      "#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6",
      "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1"
    ];
    // Hash the class name to get a consistent color
    let hash = 0;
    if (className) {
      for (let i = 0; i < className.length; i++) {
        hash = className.charCodeAt(i) + ((hash << 5) - hash);
      }
    }
    return defaultColors[Math.abs(hash) % defaultColors.length];
  };

  // ===== Helper: Draw detections on image =====
  const drawDetections = (imageSrc, detections) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        ctx.lineWidth = 5;

        detections.forEach((detection) => {
          let x, y, width, height;
          
          if (detection.bbox && Array.isArray(detection.bbox)) {
            const [x1, y1, x2, y2] = detection.bbox;
            x = x1;
            y = y1;
            width = x2 - x1;
            height = y2 - y1;
          } else if (detection.box && Array.isArray(detection.box)) {
            const [x1, y1, x2, y2] = detection.box;
            x = x1;
            y = y1;
            width = x2 - x1;
            height = y2 - y1;
          } else {
            x = detection.x || detection.x1 || 0;
            y = detection.y || detection.y1 || 0;
            width = detection.width || (detection.x2 ? detection.x2 - x : 0);
            height = detection.height || (detection.y2 ? detection.y2 - y : 0);
          }
          
          const className = detection.class_name || detection.className || "unknown";
          const color = getClassColor(className);
          
          ctx.strokeStyle = color;
          ctx.strokeRect(x, y, width, height);
        });

        resolve(canvas.toDataURL("image/png"));
      };
      img.src = imageSrc;
    });
  };

  // ===== Image processing with YOLOv8 API =====
  const processImage = async () => {
    if (!imageFile) return;

    setStatus("Processing…");
    setIsProcessing(true);
    setClaheImageUrl(null);

    try {
      let inputImageUrl;
      let base64Image;

      if (useClahePreprocess && cvReady) {
        setStatus("Applying CLAHE grayscale…");
        const originalUrl = URL.createObjectURL(imageFile);
        const claheResult = await applyCLAHE(originalUrl);
        URL.revokeObjectURL(originalUrl);
        if (claheResult && claheResult.startsWith("data:")) {
          inputImageUrl = claheResult;
          base64Image = claheResult.split(",")[1];
          setClaheImageUrl(claheResult);
        } else {
          inputImageUrl = URL.createObjectURL(imageFile);
          base64Image = await fileToBase64(imageFile);
        }
      } else {
        inputImageUrl = URL.createObjectURL(imageFile);
        base64Image = await fileToBase64(imageFile);
      }

      const response = await fetch(`${API_HOST}/detect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          image_base64: base64Image,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      const detections = data.all_detections || data.dot_detection || data.best_model || data.detections || [];
      
      console.log("API Response:", data);
      console.log("Detections:", detections);
      console.log("Detections count:", detections.length);
      
      // Extract shape_matching data
      if (data.shape_matching && Array.isArray(data.shape_matching)) {
        setShapeMatching(data.shape_matching);
      } else {
        setShapeMatching([]);
      }
      
      if (detections.length === 0) {
        setStatus("No detections found in response.");
        return;
      }
      
      const annotatedImageUrl = await drawDetections(inputImageUrl, detections);
      
      // Use best_model (ROIs/watermarks) + dot_detection directly from API
      const watermarkDetections = data.best_model || [];
      const dotDetections = data.dot_detection || [];
      const cropped = await cropImagesFromDetections(inputImageUrl, watermarkDetections, dotDetections);

      setDisplayImageUrl(annotatedImageUrl);
      setCroppedImages(cropped);
      setRoiPage(0);
      if (cropped.length > 0) {
        setStatus("Matching ROIs & merging…");
        try {
          const matches = await Promise.all(cropped.map((item) => fetchMatchForRoi(item)));
          setRoiShapeMatches(matches);
          const valid = matches.filter(Boolean);
          const merged = valid.length > 0 ? mergeRoiShapeMatches(valid) : (data.shape_matching || []);
          setShapeMatching(merged);
          setSelectedIcons(Array(9).fill(null));
          setExplicitlyCleared(new Set());
        } catch (e) {
          console.warn("Match ROI failed:", e);
          if (data.shape_matching) setShapeMatching(data.shape_matching);
        }
      }
      if (data.shape_matching && cropped.length === 0) setShapeMatching(data.shape_matching);
      setStatus(`Done. Found ${detections.length} detection(s)${cropped.length > 0 ? ` · Merged ${cropped.length} ROI(s)` : ""}.`);
    } catch (error) {
      console.error("Processing error:", error);
      setStatus(`Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const processImageAndNoticeHeightChange = async() => {
    await processImage()
   
const bodyHeight = computeClientIdButtonRef.current.offsetTop + 122
    window.top.postMessage(
      { event: "body_height", data: { bodyHeight } },
      "*"
    );
  }

  // ===== Icon grid logic =====
  const openPicker = (cellIndex) => {
    setActiveCell(cellIndex);
    setPickerOpen(true);
  };

  const selectIcon = (iconIndex) => {
    if (activeCell == null) return;
    setSelectedIcons((prev) => {
      const next = [...prev];
      next[activeCell] = iconIndex;
      return next;
    });
    // Remove from explicitly cleared set when user selects an icon
    setExplicitlyCleared((prev) => {
      const next = new Set(prev);
      next.delete(activeCell);
      return next;
    });
    setPickerOpen(false);
    setActiveCell(null);
  };

  const clearCell = (cellIndex) => {
    setSelectedIcons((prev) => {
      const next = [...prev];
      next[cellIndex] = null;
      return next;
    });
    // Mark this cell as explicitly cleared
    setExplicitlyCleared((prev) => {
      const next = new Set(prev);
      next.add(cellIndex);
      return next;
    });
  };

  // ===== Helper: Merge shape_matching từ tất cả ROI (chọn kết quả accuracy cao nhất mỗi ô) =====
  const mergeRoiShapeMatches = (allMatches) => {
    const merged = [];
    for (let gridNum = 1; gridNum <= 9; gridNum++) {
      let best = null;
      let bestAcc = -1;
      for (const sm of allMatches) {
        if (!sm || !Array.isArray(sm)) continue;
        const m = sm.find((item) => item.grid === gridNum);
        if (m && m.best_shape != null && (m.best_accuracy ?? 0) > bestAcc) {
          best = m;
          bestAcc = m.best_accuracy ?? 0;
        }
      }
      if (best) merged.push(best);
    }
    return merged;
  };

  // ===== Helper: Gọi match-with-grid cho 1 ROI =====
  const fetchMatchForRoi = async (roiItem) => {
    const bounds = roiItem.gridBounds || { x1: 1/3, x2: 2/3, y1: 1/3, y2: 2/3 };
    const res = await fetch(`${API_HOST}/match-with-grid`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        roi_box: roiItem.roiBox,
        dots: (roiItem.dotsData || []).map((d) => ({ center: [d.cx, d.cy], conf: 0.8 })),
        grid_bounds: [bounds.x1, bounds.x2, bounds.y1, bounds.y2],
      }),
    });
    const data = await res.json();
    return res.ok && data.shape_matching ? data.shape_matching : null;
  };

  // ===== Helper: Get icon index from shape_matching for a grid cell =====
  const getIconIndexFromShapeMatching = (gridIndex) => {
    // If cell was explicitly cleared by user, show empty (null)
    if (explicitlyCleared.has(gridIndex)) {
      return null;
    }
    
    // Priority: Manual selection takes precedence over API data
    // If user has manually selected an icon, use that
    if (selectedIcons[gridIndex] != null) {
      return selectedIcons[gridIndex];
    }
    
    // Otherwise, use API data from shape_matching
    if (!shapeMatching || shapeMatching.length === 0) {
      return null;
    }
    
    // Find the shape_matching entry for this grid number
    // Grid numbers are 1-9 (1-based), cells are 0-8 (0-based)
    // So: grid 1 → cell 0, grid 2 → cell 1, ..., grid 9 → cell 8
    // Mapping: grid number - 1 = cell index, or grid number = cell index + 1
    const matching = shapeMatching.find((item) => item.grid === gridIndex + 1);
    
    if (!matching) {
      return null;
    }
    
    // If best_shape is null, show empty (null)
    if (matching.best_shape === null || matching.best_shape === undefined) {
      return null;
    }
    
    // Convert best_shape (1-11) to icon index (0-10)
    // best_shape - 1 = icon index: shape 1 → icon index 0, shape 2 → icon index 1, etc.
    const iconIndex = matching.best_shape - 1;
    
    // Ensure icon index is valid (0-10)
    if (iconIndex >= 0 && iconIndex < ICON_IMAGES.length) {
      return iconIndex;
    }
    
    return null;
  };


  // ===== recoverCipherShape (single pattern) =====
  const recoverCipherShapeSingle = (pattern, clientIdAddition = 0) => {
    const cipherShapeToWordSafeMap = {
      3: "G",
      10: "F",
      11: "C",
      18: "9",
      19: "8",
      30: "7",
      35: "6",
      42: "5",
      43: "4",
      71: "3",
      100: "2",
    };

    const wordSafeToBase11Map = {
      2: "0",
      3: "1",
      4: "2",
      5: "3",
      6: "4",
      7: "5",
      8: "6",
      9: "7",
      C: "8",
      F: "9",
      G: "A",
    };

    const wordSafeToBase11ReverseMap = {
      G: "0",
      F: "1",
      C: "2",
      9: "3",
      8: "4",
      7: "5",
      6: "6",
      5: "7",
      4: "8",
      3: "9",
      2: "A",
    };

    // Convert pattern to word-safe string
    let wordSafeString = "";
    for (let patternItem of pattern) {
      // Skip "na" values
      if (patternItem === "na" || patternItem === null || patternItem === undefined) {
        continue;
      }
      // Convert pattern item to number for lookup (handles both string and number)
      const patternNum = typeof patternItem === 'string' ? parseInt(patternItem, 10) : patternItem;
      if (!isNaN(patternNum) && cipherShapeToWordSafeMap[patternNum]) {
        wordSafeString += cipherShapeToWordSafeMap[patternNum];
      }
    }
    if (wordSafeString.length === 0) {
      return 0;
    }

    // Convert word-safe string to base11 string
    let base11String = "";
    wordSafeString.split("").forEach((char, index) => {
      if (index % 2 == 0) {
        base11String += wordSafeToBase11Map[char] || "?";
      } else {
        base11String += wordSafeToBase11ReverseMap[char] || "?";
      }
    });

    if (base11String.indexOf("?") >= 0) {
      return 0;
    }

    const parsed = parseInt(base11String, 11);
    if (Number.isNaN(parsed)) {
      return 0;
    }

    const clientId = parsed - clientIdAddition;
    return clientId;
  };

  // ===== Download CLAHE image =====
  const downloadClaheImage = () => {
    if (!claheImageUrl) return;
    
    const link = document.createElement("a");
    link.href = claheImageUrl;
    link.download = `clahe-processed-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleComputeClientId = () => {
    // Use the same logic as display - get icon from shapeMatching or selectedIcons
    const pattern = selectedIcons.map((_, gridIndex) => {
      const iconIndex = getIconIndexFromShapeMatching(gridIndex);
      if (iconIndex == null) return "na";
      const shapeCode = PATTERN_LIST[iconIndex] ?? "na";
      return shapeCode;
    });

    console.log("Pattern for calculation:", pattern);
    const id = recoverCipherShapeSingle(pattern, 0);
    console.log("Calculated Client ID:", id);
    setClientId(id);

    window.top.postMessage(
      { event: "compute_clientid", data: { clientId: id } },
      "*"
    );
  };

  return (
    <div className="app-page">
      <div className="app-root">
        <header className="app-header">
          <div>
            <h1>Cipher Dot Detection</h1>
            <p className="status">{status}</p>
          </div>
        </header>

        {/* ===== Image section ===== */}
        <section className="section-card">
          <div className="section-header">
            <h2>Input & Processing</h2>
          </div>

          <div className="controls-row">
            <div className="controls-group controls-input">
              <label className="file-input-label">
                <span>Choose image</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                />
              </label>
              <label className="clahe-option" title="Apply CLAHE grayscale - enhances contrast for better dot detection">
                <input
                  type="checkbox"
                  checked={useClahePreprocess}
                  onChange={(e) => setUseClahePreprocess(e.target.checked)}
                />
                <span>Preprocessing</span>
              </label>
            </div>
            <button
              className="btn-primary btn-process"
              onClick={processImageAndNoticeHeightChange}
              disabled={!imageFile}
            >
              ▶ Process Image
            </button>
          </div>

          <div className="preview-layout">
            <div className="panel">
              <h3>Image</h3>
              <div className="panel-body">
                {displayImageUrl ? (
                  <>
                    <div className="output-image-wrapper">
                      <img
                        src={displayImageUrl}
                        alt="Processed image"
                        className="output-image"
                      />
                      {isProcessing && (
                        <div className="scanning-overlay">
                          <div className="scanning-line"></div>
                        </div>
                      )}
                    </div>
                    {claheImageUrl && (
                      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                        <button
                          className="btn-ghost"
                          onClick={downloadClaheImage}
                          style={{ padding: '0.5rem 1rem' }}
                        >
                          ⬇ Download CLAHE Grayscale Image
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="no-output">
                    No image loaded. Choose an image and click{" "}
                    <strong>Process Image</strong>.
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ===== Detected Objects ===== */}
        {croppedImages.length > 0 && (
          <section className="section-card">
            <div className="section-header">
              <div>
                <h2>Detected Objects</h2>
                <p className="section-subtitle">ROIs with dots · 3×3 grid · 9 per page</p>
              </div>
              <div className="roi-pagination">
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => setRoiPage((p) => Math.max(0, p - 1))}
                  disabled={roiPage === 0}
                >
                  ← Back
                </button>
                <span className="roi-page-info">
                  {roiPage + 1} / {Math.ceil(croppedImages.length / 9) || 1}
                </span>
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => setRoiPage((p) => Math.min(Math.ceil(croppedImages.length / 9) - 1, p + 1))}
                  disabled={roiPage >= Math.ceil(croppedImages.length / 9) - 1}
                >
                  Next →
                </button>
              </div>
            </div>
            <p className="detected-objects-guide">
              <strong>Tip:</strong> Drag the green grid lines on each image to align the 3×3 grid with the dot pattern. 
              Correct alignment ensures accurate Client ID detection. Changes auto-save when you release the mouse.
            </p>
            <div className="detected-objects-layout">
              <div className="detected-roi-grid">
                {croppedImages.slice(roiPage * 9, roiPage * 9 + 9).map((item, i) => {
                  const actualIndex = roiPage * 9 + i;
                  return (
                  <RoiCell
                    key={item.id}
                    item={item}
                    index={actualIndex}
                    onSaveEdit={(newBounds, newDataUrl) => {
                      setCroppedImages((prev) => {
                        const next = [...prev];
                        next[actualIndex] = { ...next[actualIndex], gridBounds: newBounds, dataUrl: newDataUrl };
                        return next;
                      });
                    }}
                    onRematch={async (newBounds) => {
                      setRematchLoading(true);
                      try {
                        const res = await fetch(`${API_HOST}/match-with-grid`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            roi_box: item.roiBox,
                            dots: (item.dotsData || []).map((d) => ({ center: [d.cx, d.cy], conf: 0.8 })),
                            grid_bounds: [newBounds.x1, newBounds.x2, newBounds.y1, newBounds.y2],
                          }),
                        });
                        const data = await res.json();
                        if (!res.ok) {
                          console.error("Rematch API error:", data);
                          return;
                        }
                        if (data.shape_matching && Array.isArray(data.shape_matching)) {
                          setRoiShapeMatches((prev) => {
                            const next = [...prev];
                            next[actualIndex] = data.shape_matching;
                            setShapeMatching(mergeRoiShapeMatches(next.filter(Boolean)));
                            return next;
                          });
                          setSelectedIcons(Array(9).fill(null));
                          setExplicitlyCleared(new Set());
                        }
                      } catch (err) {
                        console.error("Rematch error:", err);
                      } finally {
                        setRematchLoading(false);
                      }
                    }}
                    rematchLoading={rematchLoading}
                  />
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ===== Pattern section ===== */}
        <section className="section-card">
          <div className="section-header">
            <h2>Pattern Grid → Client ID</h2>
          </div>

          <p className="section-subtext">
            Choose shapes for 9 slots (empty = <code>na</code>). Then
            compute client ID using your cipher mapping.
          </p>

          <div className="pattern-layout">
            <div className="pattern-grid-card">
              <div className="cell-grid">
                {selectedIcons.map((_, i) => {
                  // Get icon index from shape_matching, fallback to selectedIcons
                  const iconIndex = getIconIndexFromShapeMatching(i);
                  return (
                    <div key={i} className="cell-row">
                      <div className="cell">
                        <div className="cell-preview">
                          {iconIndex != null ? (
                            <img
                              src={ICON_IMAGES[iconIndex]}
                              alt={`icon-${iconIndex}`}
                            />
                          ) : (
                            <span className="cell-empty">
                              Empty
                            </span>
                          )}
                        </div>

                        {iconIndex != null && (
                          <button
                            className="cell-clear"
                            onClick={() => clearCell(i)}
                            title="Clear"
                          >
                            ×
                          </button>
                        )}
                      </div>

                      <button
                        className="cell-dropdown"
                        onClick={() => openPicker(i)}
                        title="Select icon"
                      >
                        ▾
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="pattern-actions">
                <button
                  ref={computeClientIdButtonRef}
                  className="btn-primary"
                  onClick={handleComputeClientId}
                >
                  <svg className="btn-lock-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> Compute Client ID
                </button>
                {clientId !== null && (
                  <span className="client-id-display">
                    Client ID: <strong>{clientId}</strong>
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Icon picker popup */}
        {pickerOpen && (
          <div
            className="picker-backdrop"
            onClick={() => setPickerOpen(false)}
          >
            <div
              className="picker-box"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="picker-header">
                <span>
                  Select icon for cell{" "}
                  {activeCell != null ? activeCell + 1 : ""}
                </span>
                <button
                  className="picker-close"
                  onClick={() => setPickerOpen(false)}
                >
                  ×
                </button>
              </div>

              <div className="icon-grid">
                {ICON_IMAGES.map((src, idx) => (
                  <button
                    key={idx}
                    className="icon-grid-item"
                    onClick={() => selectIcon(idx)}
                  >
                    <img src={src} alt={`icon-${idx + 1}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
